import React, { useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha } from '@mui/material/styles';
import { useTranslation } from '../app/LanguageContext';
import { isVideoMimeOrExtension } from '../utils/mediaMime';
import { getFileIcon, formatFileSize } from '../utils/mediaDisplay';

const SWIPE_THRESHOLD_PX = 50;

function isArrowNavBlockedTarget(el) {
  if (!el) return false;
  if (el.closest?.('video')) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON';
}

/**
 * Fullscreen media preview with dark chrome, keyboard and optional touch swipe.
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {Array} items — ordered carousel (full gallery/page list for parity with MediaGallery)
 * @param {object|null} activeMedia
 * @param {function} onNavigate — (media: object) => void
 * @param {string} apiBaseUrl — e.g. REACT_APP_API_URL
 */
export default function MediaLightbox({
  open,
  onClose,
  items,
  activeMedia,
  onNavigate,
  apiBaseUrl,
}) {
  const { translate } = useTranslation();
  const rootRef = useRef(null);
  const touchStartX = useRef(null);

  const index =
    open && activeMedia ? items.findIndex((m) => m.id === activeMedia.id) : -1;
  const showNav = index >= 0 && items.length > 1;

  useEffect(() => {
    if (!open || !activeMedia) return;
    const t = requestAnimationFrame(() => {
      rootRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(t);
  }, [open, activeMedia]);

  const goBy = useCallback(
    (delta) => {
      if (!showNav || index < 0) return;
      const next = index + delta;
      if (next < 0 || next >= items.length) return;
      onNavigate(items[next]);
    },
    [showNav, index, items, onNavigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      if (isArrowNavBlockedTarget(e.target)) return;
      const delta = e.key === 'ArrowLeft' ? -1 : 1;
      if (!showNav) return;
      const next = index + delta;
      if (next < 0 || next >= items.length) return;
      e.preventDefault();
      onNavigate(items[next]);
    }
  };

  const onTouchStart = (e) => {
    if (!showNav || !e.touches?.length) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (!showNav || touchStartX.current == null || !e.changedTouches?.length) {
      touchStartX.current = null;
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > SWIPE_THRESHOLD_PX) goBy(-1);
    else if (dx < -SWIPE_THRESHOLD_PX) goBy(1);
  };

  /** Top bar + bottom metadata bar + padding (safe areas added in calc). */
  const mediaMaxHeight =
    'calc(100dvh - 120px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';
  const videoMaxHeight =
    'calc(100dvh - 184px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      scroll="paper"
      PaperProps={{
        sx: (theme) => ({
          bgcolor: alpha(theme.palette.grey[900], 0.94),
          color: theme.palette.common.white,
          display: 'flex',
          flexDirection: 'column',
          m: 0,
          maxHeight: '100%',
          overflow: 'hidden',
        }),
      }}
    >
      <Box
        ref={rootRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          outline: 'none',
          pt: 'env(safe-area-inset-top, 0px)',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            px: 1,
            py: 0.5,
          }}
        >
          <IconButton
            aria-label={translate('common.close')}
            onClick={onClose}
            size="small"
            sx={{
              color: 'common.white',
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.35),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.5) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 1, sm: 2 },
            pt: 0.5,
            pb: 1,
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {showNav && (
            <>
              <IconButton
                aria-label={translate('media.preview_previous')}
                onClick={() => goBy(-1)}
                disabled={index <= 0}
                sx={(theme) => ({
                  position: 'absolute',
                  left: { xs: 4, sm: 12 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  color: 'common.white',
                  opacity: 0.45,
                  transition: theme.transitions.create('opacity'),
                  bgcolor: alpha(theme.palette.common.black, 0.25),
                  '&:hover': { opacity: 1, bgcolor: alpha(theme.palette.common.black, 0.45) },
                  '&.Mui-disabled': {
                    opacity: 0.15,
                    color: alpha(theme.palette.common.white, 0.35),
                  },
                })}
                size="large"
              >
                <ChevronLeftIcon fontSize="inherit" />
              </IconButton>
              <IconButton
                aria-label={translate('media.preview_next')}
                onClick={() => goBy(1)}
                disabled={index >= items.length - 1}
                sx={(theme) => ({
                  position: 'absolute',
                  right: { xs: 4, sm: 12 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  color: 'common.white',
                  opacity: 0.45,
                  transition: theme.transitions.create('opacity'),
                  bgcolor: alpha(theme.palette.common.black, 0.25),
                  '&:hover': { opacity: 1, bgcolor: alpha(theme.palette.common.black, 0.45) },
                  '&.Mui-disabled': {
                    opacity: 0.15,
                    color: alpha(theme.palette.common.white, 0.35),
                  },
                })}
                size="large"
              >
                <ChevronRightIcon fontSize="inherit" />
              </IconButton>
            </>
          )}

          {activeMedia && (
            <Fade in timeout={220} key={activeMedia.id}>
              <Box
                sx={{
                  maxWidth: '100%',
                  maxHeight: mediaMaxHeight,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                }}
              >
                {activeMedia.mime_type?.startsWith('image/') ? (
                  <Box
                    component="img"
                    src={`${apiBaseUrl}${activeMedia.file_path}`}
                    alt={activeMedia.filename}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: mediaMaxHeight,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                ) : isVideoMimeOrExtension(activeMedia.mime_type, activeMedia.filename) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                      maxHeight: mediaMaxHeight,
                    }}
                  >
                    <Box
                      component="video"
                      controls
                      playsInline
                      preload="metadata"
                      src={`${apiBaseUrl}${activeMedia.file_path}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: videoMaxHeight,
                        width: '100%',
                      }}
                    />
                    <Button
                      variant="outlined"
                      component="a"
                      href={`${apiBaseUrl}${activeMedia.file_path}`}
                      download={activeMedia.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'common.white', borderColor: alpha('#fff', 0.5) }}
                    >
                      {translate('media.download_file')}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ py: 2 }}>
                    <Box sx={{ mb: 2 }}>{getFileIcon(activeMedia.mime_type, activeMedia.filename, 56)}</Box>
                    <Button
                      variant="contained"
                      component="a"
                      href={`${apiBaseUrl}${activeMedia.file_path}`}
                      download={activeMedia.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {translate('media.download_file')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        {activeMedia && (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderTop: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.35),
            }}
          >
            {getFileIcon(activeMedia.mime_type, activeMedia.filename, 36)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={activeMedia.filename}>
                {activeMedia.filename}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.65) }} component="div">
                {formatFileSize(activeMedia.file_size || 0)} · {activeMedia.mime_type || '—'}
                {showNav && (
                  <>
                    {' · '}
                    {index + 1} / {items.length}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
